"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dashboardViewSchema = exports.dashboardRecentLogSchema = exports.dashboardGapTaskSchema = exports.TASK_GAPS = exports.dashboardDaySchema = void 0;
const zod_1 = require("zod");
const working_day_1 = require("./working-day");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Kezdőlap — the home screen the mockup draws and we never built.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `markup.html` 285–394. Four stat cards, an hours chart with a week and a month rendering, the
 * caller's most recent entries, and the list of their own tasks with something missing.
 *
 * ⚠️ **Everything here is the CALLER's own.** The mockup's home is a personal page — *"Rögzített óra
 * a héten"*, *"Nyitott feladataim"*, *"Aktív projektjeim"*, *"Legutóbbi rögzítéseim"* — not a
 * workspace summary. That is what makes it a home rather than a report.
 */
/**
 * A day on the hours chart.
 *
 * ⚠️⚠️ **ONE SHAPE FOR BOTH RENDERINGS, WHICH IS WHY THIS IS DAYS RATHER THAN BARS.** The mockup
 * draws the week as seven columns and the month as a calendar heat grid — different pictures of the
 * same series. Returning a pre-bucketed `bars` array (as `/worklogs/overview` does for its own
 * chart) would need two endpoints or a `kind` discriminator, and the client would still have to
 * know the calendar to lay the month out.
 */
exports.dashboardDaySchema = zod_1.z.object({
    /** ISO wire date. */
    date: zod_1.z.string(),
    /** Decimal hours, as a string — the same wire form every other hours field uses. */
    hours: zod_1.z.string(),
    /**
     * ⚠️⚠️ **C50 · WHAT THE DAY EXPECTED, AND THE KERNEL'S VERDICT ON IT.** The chart drew hours
     * against nothing until now, so a bar said *"you logged six"* and left *"of what?"* to the
     * reader. Both come from C44 and neither is computed here — the home screen adds no arithmetic
     * of its own, which is the chunk's own rule.
     *
     * ⚠️ `planned` is `null` for somebody with no schedule covering the day, which is *nincs napi
     * terv* rather than a plan of zero.
     */
    planned: zod_1.z.string().nullable(),
    verdict: zod_1.z.enum(working_day_1.DAY_VERDICTS),
});
/**
 * Why a task counts as incomplete.
 *
 * ⚠️⚠️ **CODES, NOT SENTENCES — RULE 7.** The mockup stores the words (`'Nincs becslés'`,
 * `'Lejárt határidő'`) because it has one language. Ours cannot: the client turns each code into a
 * chip from the catalogue, in the reader's own locale.
 *
 * ⚠️ **`no_tags` is included and `task_tags` exists**, checked against the schema rather than
 * assumed from the mockup's `t.tags`.
 */
exports.TASK_GAPS = [
    'no_schedule',
    'no_estimate',
    'no_description',
    'no_assignee',
    'no_project',
    'no_tags',
    /** In progress with nothing logged against it. */
    'no_hours',
    'overdue',
];
exports.dashboardGapTaskSchema = zod_1.z.object({
    id: zod_1.z.string(),
    key: zod_1.z.string(),
    title: zod_1.z.string(),
    projectName: zod_1.z.string().nullable(),
    projectColor: zod_1.z.string().nullable(),
    gaps: zod_1.z.array(zod_1.z.enum(exports.TASK_GAPS)),
});
exports.dashboardRecentLogSchema = zod_1.z.object({
    id: zod_1.z.string(),
    taskKey: zod_1.z.string(),
    taskTitle: zod_1.z.string(),
    projectName: zod_1.z.string().nullable(),
    projectColor: zod_1.z.string().nullable(),
    hours: zod_1.z.string(),
});
exports.dashboardViewSchema = zod_1.z.object({
    /**
     * The four figures.
     *
     * ⚠️ **`weekHours` is `H:MM` and the rest are counts**, matching the mockup's own mix — its first
     * card has no unit because the value already carries one.
     */
    weekHours: zod_1.z.string(),
    openTasks: zod_1.z.number().int(),
    gapTaskCount: zod_1.z.number().int(),
    activeProjects: zod_1.z.number().int(),
    /** The chart's series, and the period it covers. */
    days: zod_1.z.array(exports.dashboardDaySchema),
    /** ISO dates, inclusive. The screen renders the label; the server decides the range. */
    from: zod_1.z.string(),
    to: zod_1.z.string(),
    recentLogs: zod_1.z.array(exports.dashboardRecentLogSchema),
    /**
     * ⚠️ **Six at most, worst first** — the mockup's own `slice(0, 6)` after sorting by gap count. A
     * home screen that lists forty incomplete tasks is a backlog, not a nudge.
     */
    gapTasks: zod_1.z.array(exports.dashboardGapTaskSchema),
    /**
     *    * ┌───────────────────────────────────────────────────────────────────────┐
     *    * │ C50 · the three sections the mockup adds, and not one of them is new arithmetic.
     *    * └───────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **EVERY FIGURE BELOW IS ALREADY COMPUTED BY C44, C47 OR C48.** The chunk's own row says the
     * home screen adds none of its own, and that is the property that keeps it from disagreeing with
     * the screens it summarises: a person reading *"3 nap függőben"* here and opening Kérelmeim must
     * see the same three.
     */
    /** ⚠️ Today's plan, or `null` for *nincs napi terv*. The today strip's whole content. */
    todayPlanned: zod_1.z.string().nullable(),
    /**
     * The weekly frame and what the week holds so far — ⚠️ **both `null` unless this person is on a
     * `flex` schedule** (P3-7). Their `todayPlanned` is always `null`, so without these the strip
     * would say *nincs napi terv* and stop, which tells somebody measured weekly nothing at all.
     */
    weekFrame: zod_1.z.string().nullable(),
    weekLogged: zod_1.z.string().nullable(),
    /** The caller's own pending requests - leave and overtime together, as the queue shows them. */
    pendingRequests: zod_1.z.number().int().nonnegative(),
    /** Approved leave that has not started yet. *"Közelgő szabadság"*. */
    upcomingLeaveDays: zod_1.z.number().int().nonnegative(),
    /** One row per quota type: what they have, what they have spent, what is awaiting a decision. */
    quotas: zod_1.z.array(zod_1.z.object({
        leaveTypeName: zod_1.z.string(),
        /** The type's own icon, so the card can draw it without loading the type list. */
        icon: zod_1.z.string(),
        quotaDays: zod_1.z.number().int().nullable(),
        usedDays: zod_1.z.number().int().nonnegative(),
        pendingDays: zod_1.z.number().int().nonnegative(),
        /** ⚠️ Already inside `quotaDays`, and reported again so the card can say where it came from. */
        carriedDays: zod_1.z.number().int().nonnegative(),
    })),
    /**
     * This month's overtime, in the mockup's own three figures.
     *
     * ⚠️ **`accountedHours` is hours × multiplier**, and the multiplier is the frozen one wherever a
     * claim has been approved (P3-5). Submitted counts the claims; the other two count hours.
     */
    overtimeSubmitted: zod_1.z.number().int().nonnegative(),
    overtimeApprovedHours: zod_1.z.string(),
    overtimeAccountedHours: zod_1.z.string(),
    /* ── the mockup's four sections, in the figures each of them actually draws ─────────────── */
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ `Időlogok` — FOUR STATS AND THE MONTH IS ONE OF THEM.            │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * `homeStats` in the mockup: *Ma*, *Ez a hét*, *2026. aug*, *Hiányos napok*. We shipped four
     * unrelated counters (week hours, open tasks, gap tasks, active projects) above a chart, which is
     * the Phase 1 home screen — Phase 3 folds those into the sections that own them and leads with
     * the working-time picture instead.
     */
    todayHours: zod_1.z.string(),
    weekPlanned: zod_1.z.string().nullable(),
    monthLabel: zod_1.z.string(),
    monthHours: zod_1.z.string(),
    /** ⚠️ Planned **to date**, not for the whole month: a month half over is not a shortfall. */
    monthPlannedToDate: zod_1.z.string().nullable(),
    /** Days already past where the person logged less than the plan — the mockup's `missDays`. */
    missingDays: zod_1.z.number().int().nonnegative(),
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ 16 Oct 2026 · the reworked homescreen's *Időlogok*: THE WEEK, NOT THE MONTH.
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * `h2Stats` reads *Ma*, *Ez a hét*, *Hátra a héten* and *Hiányos napok · a héten*. The month
     * figures above stay on the wire for the agent's home tool; the screen reads these.
     *
     * ⚠️ **`weekLeft` is plan minus logged, SIGNED** — negative is *Terven felül*, which the tile
     * labels differently and draws as a magnitude. `null` with no plan to measure against.
     */
    weekHoursLogged: zod_1.z.string(),
    weekLeft: zod_1.z.string().nullable(),
    /** The current week's past days logged short of their plan — the same kernel verdict. */
    weekMissingDays: zod_1.z.number().int().nonnegative(),
    /**
     * `Szabadság` — the quota cards, and the two lists under them.
     *
     * ⚠️ The icon and the quota source travel WITH each row: the card draws a bar only for a type
     * with a frame, and an unlimited one shows what has been taken instead of `n / 0`.
     */
    pendingRows: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string(),
        code: zod_1.z.string(),
        leaveTypeName: zod_1.z.string(),
        fromDate: zod_1.z.string(),
        toDate: zod_1.z.string(),
        days: zod_1.z.number().int().nonnegative(),
        status: zod_1.z.string(),
    })),
    upcomingRows: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string(),
        code: zod_1.z.string(),
        leaveTypeName: zod_1.z.string(),
        fromDate: zod_1.z.string(),
        toDate: zod_1.z.string(),
        days: zod_1.z.number().int().nonnegative(),
        status: zod_1.z.string(),
    })),
    /**
     * `Túlóra` — the four figures and the last claims.
     *
     * ⚠️ **`accounted` is hours × the frozen multiplier** (P3-5), which is why it is beside the plain
     * hours rather than instead of them: the pair is what an overtime section exists to show.
     */
    overtimeYearHours: zod_1.z.string(),
    overtimeYearAccounted: zod_1.z.string(),
    overtimePendingHours: zod_1.z.string(),
    overtimePendingCount: zod_1.z.number().int().nonnegative(),
    overtimeMonthHours: zod_1.z.string(),
    overtimeMonthCount: zod_1.z.number().int().nonnegative(),
    /** Claims waiting on THIS person as their approver — the mockup's *Elbírálásra vár nálam*. */
    overtimeWaitingOnMe: zod_1.z.number().int().nonnegative(),
    overtimeRows: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string(),
        code: zod_1.z.string(),
        workedOn: zod_1.z.string(),
        taskKey: zod_1.z.string().nullable(),
        /** ⚠️ Both null until an approver prices the claim — `0147`. */
        overtimeTypeName: zod_1.z.string().nullable(),
        hours: zod_1.z.string(),
        multiplier: zod_1.z.string().nullable(),
        status: zod_1.z.string(),
        /**
         * 29 Oct 2026 · how an APPROVED claim settled — `0146`: a payout at the rate, or leave days
         * credited to a quota. All three null until approved, and the days and the type null on a
         * payout. Added under rule 8 so the home card (and the agent) can say *"became 1 day of
         * Fizetett szabadság"* rather than printing a rate beside a claim that paid nothing.
         */
        settlement: zod_1.z.enum(['payout', 'credit']).nullable(),
        creditedDays: zod_1.z.string().nullable(),
        creditLeaveTypeName: zod_1.z.string().nullable(),
    })),
    /**
     * 16 Oct 2026 · the reworked *Túlóra* card's two lists: every claim still **pending**, and the
     * ones **approved** on work done in the last thirty days (`h2OtPendRows`, `h2OtOkRows`). Newest
     * first, eight each — a home card, not the queue.
     */
    overtimePendingRows: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string(),
        workedOn: zod_1.z.string(),
        taskKey: zod_1.z.string().nullable(),
        overtimeTypeName: zod_1.z.string().nullable(),
        hours: zod_1.z.string(),
        multiplier: zod_1.z.string().nullable(),
    })),
    overtimeApprovedRows: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string(),
        workedOn: zod_1.z.string(),
        taskKey: zod_1.z.string().nullable(),
        overtimeTypeName: zod_1.z.string().nullable(),
        hours: zod_1.z.string(),
        multiplier: zod_1.z.string().nullable(),
    })),
    /**
     * `Feladatok` — the status chips, my open tasks and the gap list.
     *
     * ⚠️ **The chips are the workspace's own statuses with their colours**, not a fixed set: a
     * workspace that renamed *Folyamatban* sees its own word, and the count is what a person would
     * find on `Feladataim` after clicking through.
     */
    taskChips: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string(),
        name: zod_1.z.string(),
        color: zod_1.z.string(),
        count: zod_1.z.number().int().nonnegative(),
    })),
    myTasks: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.string(),
        key: zod_1.z.string(),
        title: zod_1.z.string(),
        projectName: zod_1.z.string().nullable(),
        projectColor: zod_1.z.string().nullable(),
        trackedHours: zod_1.z.string(),
        estimateHours: zod_1.z.string().nullable(),
        dueDate: zod_1.z.string().nullable(),
    })),
    overdueTasks: zod_1.z.number().int().nonnegative(),
    dueTodayTasks: zod_1.z.number().int().nonnegative(),
});
//# sourceMappingURL=dashboard.js.map